import { get } from 'lodash-es';

// Redux
import createStore from 'redux/lib/createStore';
import compose from 'redux/lib/compose';
import applyMiddleware from 'redux/lib/applyMiddleware';
import combineReducers from 'redux/lib/combineReducers';
import { connectRoutes } from 'redux-first-router';
import { createHashHistory } from 'history';
import { persistStore, persistReducer } from 'redux-persist';
import storage from 'redux-persist/lib/storage/session';
import { createLogicMiddleware } from 'redux-logic';

// Reducers
import appReducer, {
  actions as appActions,
  types as appTypes,
} from './ducks/app.js';
import authReducer, {
  types as authTypes,
  actions as authActions,
} from './ducks/auth.js';
import routingReducer, { routesMap, options } from './ducks/routing.js';
import customerReducer from './ducks/customer.js';
import propertiesReducer from './ducks/properties';

// Logic
import customerLogic from './logic/customer.js';
import propertiesLogic from './logic/properties.js';
import authLogic from './logic/auth.js';

export default function configureStore() {
  options.displayConfirmLeave = (message, callback) => {
    const canLeave = (can) => {
      store.dispatch({ type: 'APP/HIDE_BLOCK_NAVIGATION_MODEL' }); // hide modal
      if (!can) {
        // If user closes confirm leave dialog, then we remove the signout.
        store.dispatch(authActions.clearSignout());
      } else if (can && store.getState().auth.signout) {
        // Sign the user out from the confirm leave nav dialog if it appeared from the signout action.
        store.dispatch(authActions.signout());
      }
      return callback(can); // navigate to next route or stay where you're at
    };

    if (!get(store.getState(), 'auth.forceSignout', false)) {
      store.dispatch({
        type: 'APP/SHOW_BLOCK_NAVIGATION_MODAL',
        payload: { message, canLeave },
      });
    }
  };
  options.createHistory = () => createHashHistory();
  const location = connectRoutes(routesMap, { ...options });

  // Add our logic to middleware
  const logicMiddleware = createLogicMiddleware([
    ...customerLogic,
    ...authLogic,
    ...propertiesLogic,
  ]);

  const topReducer = combineReducers({
    app: appReducer,
    auth: authReducer,
    location: location.reducer,
    routing: routingReducer,
    customer: customerReducer,
    properties: propertiesReducer,
  });

  // Wrap app reducer in root reducer to enable clearing of value on signout
  const rootReducer = (state, action) => {
    return topReducer(state, action);
  };

  // Setup store persistence
  const persistConfig = {
    key: 'root',
    storage: storage,
    whitelist: ['auth'],
  };
  const persistedReducer = persistReducer(persistConfig, rootReducer);

  let devTools = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__;

  if (devTools) {
    devTools = window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__({
      actionsBlacklist: [authTypes.TICK, appTypes.USER_INTERACTION],
      features: {
        persist: false,
        dispatch: true,
        jump: true,
      },
    });
  }

  const composeEnhancers = devTools || compose;

  const store = createStore(
    persistedReducer,
    composeEnhancers(
      location.enhancer,
      applyMiddleware(logicMiddleware, location.middleware)
    )
  );

  const persistor = persistStore(
    store,
    // We need to dispatch intial location action after rehydrate
    null,
    location.initialDispatch
  );

  // Setup activity tracking
  document.onclick = function() {
    store.dispatch(appActions.userInteraction());
  };

  document.onmousemove = function() {
    store.dispatch(appActions.userInteraction());
  };

  document.onkeypress = function() {
    store.dispatch(appActions.userInteraction());
  };

  document.ontouchstart = function() {
    store.dispatch(appActions.userInteraction());
  };

  store.dispatch(authActions.tick());
  setInterval(function() {
    store.dispatch(authActions.tick());
  }, 1000);

  return { store, persistor };
}
